import * as React from "react";
import { cva, type VariantProps } from "../../lib/cva";
import { Box } from "../../components/ui/box";
import { cn } from "../../lib/utils";
import { useEscapeDismiss } from "../../hooks/use-escape-dismiss";
import { useIsMobile } from "../../hooks/use-media-query";
import type { Omittable } from "../../lib/props";

export type PanelOverlaySide = "right" | "left";

export const panelOverlayVariants = cva("border-border shadow-lg", {
  variants: {
    size: {
      sm: "w-80", // 320px
      md: "w-[28rem]", // 448px
      lg: "w-[35rem]", // 560px
    },
  },
  defaultVariants: {
    size: "md",
  },
});

// Fill only — the panel keeps its own side border and shadow, so this is a
// static bg map rather than `Box surface=` (which would bring the surface's
// border color and shadow family along).
export const panelOverlaySurfaceVariants = cva("", {
  variants: {
    surface: {
      background: "bg-background",
      sunken: "bg-sunken",
      card: "bg-card",
      muted: "bg-muted",
      popover: "bg-popover",
    },
  },
  defaultVariants: {
    surface: "background",
  },
});

// Backdrop fade — kept in sync with the panel's 200ms slide.
const BACKDROP_ANIMATION = "animate-in fade-in-0 duration-base";

export type PanelOverlayProps = Omittable<{
  /** Which edge to anchor the panel to. Default: 'right' */
  side?: PanelOverlaySide;
  /** Panel width preset. Default: 'md' (448px) */
  size?: VariantProps<typeof panelOverlayVariants>["size"];
  /**
   * Panel fill. Default 'background' — a workspace pane whose form controls
   * assume the page color. 'sunken' recesses the panel so it reads as its own
   * well against a lighter host; 'muted' and 'card' lift it instead — in dark
   * mode each sits LIGHTER than the page fill, so the panel reads raised over
   * its host ('muted' is the gentle step). 'popover' is the LOUDEST lift —
   * dark popovers sit past the card rung, the transient-menu plane — so it
   * belongs on a panel that must read as floating chrome, not as a form pane.
   */
  surface?: VariantProps<typeof panelOverlaySurfaceVariants>["surface"];
  /**
   * When provided, renders a backdrop behind the panel. Clicking the
   * backdrop calls this callback (typically to close the panel).
   */
  onDismiss?: () => void;
  /**
   * Dim the host under the backdrop. Default true. False keeps the
   * click-outside dismiss target but leaves the host content undimmed —
   * for a panel that reads as a sibling pane rather than a modal layer.
   */
  scrim?: boolean;
  children?: React.ReactNode;
}>;

/**
 * An absolutely-positioned panel that overlays its positioned container,
 * anchored to one edge and spanning full height. On mobile it fills the
 * container instead of sliding in from one side.
 *
 * The parent must be `position="relative"` — Box is `position: static` by
 * default, so the panel would otherwise escape to the nearest positioned
 * ancestor.
 *
 * This is NOT a dialog (no portal, no focus trap, no backdrop layer of its own)
 * — it overlays one container, which is why `Sheet`/`Drawer` cannot express it.
 * Domain-free, hence the particle tier.
 *
 * With `onDismiss` the panel also closes on Escape, and it takes the key
 * BEFORE any dialog hosting it — the first press closes the panel, the next
 * reaches the dialog. See `useEscapeDismiss`.
 */
export function PanelOverlay({
  side = "right",
  size,
  surface,
  onDismiss,
  scrim = true,
  children,
}: PanelOverlayProps) {
  const isMobile = useIsMobile();
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  useEscapeDismiss({ ref: rootRef, active: !!onDismiss, onDismiss });

  // On mobile: fill container. On desktop: fixed width panel with border.
  // The default fill is `background`, not `card`: the panel is a workspace
  // pane sliding over dialog content, and the page fill keeps its form
  // controls (which assume the page color behind them) reading correctly.
  const panelClasses = cn(
    panelOverlaySurfaceVariants({ surface }),
    "flex flex-col overflow-hidden",
    isMobile
      ? "flex-1"
      : cn(
          side === "right" ? "border-l" : "border-r",
          panelOverlayVariants({ size }),
        ),
  );

  // Slide in from the anchored edge on mount. Mobile is full-bleed with no
  // edge to slide from, so fade instead.
  const panelAnimation = isMobile
    ? BACKDROP_ANIMATION
    : cn(
        "animate-in duration-base",
        side === "right" ? "slide-in-from-right" : "slide-in-from-left",
      );

  if (onDismiss) {
    return (
      <Box
        ref={rootRef}
        // z-50 establishes a stacking context above overlaid content
        // (e.g. DataTable sticky headers z-20/z-30 and bulk-actions bar z-50).
        position="absolute"
        inset={0}
        zIndex={50}
        // Mobile is full-bleed with no scrim, so it stays a plain block. On
        // desktop the scrim and the panel are a row: the panel sits on the
        // anchored edge and the scrim takes the rest, so the row REVERSES for
        // a left-anchored panel.
        direction={
          isMobile ? undefined : side === "right" ? "row" : "row-reverse"
        }
      >
        {!isMobile && (
          <Box flex={1}
            as="button"
            aria-label="Close panel"
            onClick={onDismiss}
            className={scrim ? cn("bg-black/30", BACKDROP_ANIMATION) : undefined}
          />
        )}
        <Box fill="height" className={cn(panelClasses, panelAnimation)}>
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      position="absolute"
      zIndex={50}
      className={cn(
        // Mobile fills; desktop anchors to the side with a fixed width
        // (panelClasses). `inset-0` would pin left+right and override the
        // width, so only fill on mobile.
        isMobile
          ? "inset-0"
          : cn("inset-y-0", side === "right" ? "right-0" : "left-0"),
        panelClasses,
        panelAnimation,
      )}
    >
      {children}
    </Box>
  );
}
